/**
 * Workflow Diagram
 *
 *                ┌─────────────────────────┐
 *                │   User Interaction       │
 *                │ (Click / KeyDown Event)  │
 *                └─────────────┬───────────┘
 *                              │
 *                              ▼
 *                ┌─────────────────────────┐
 *                │    handleClick()        │
 *                └─────────────┬───────────┘
 *                 ┌────────────┼─────────────────────────────┐
 *                 │            │                             │
 *                 ▼            ▼                             ▼
 *        miniSB === true   hasChildren === true        Default (no children)
 *            │                  │                             │
 *   setIndex(item.id)    Toggle open state             buttonClick(`/${item.to}`)
 *                              │
 *          ┌───────────────────┼───────────────────┐
 *          │                   │                   │
 *   filterBar === true   selected === true     miniSB === true
 *        │                   │                   │
 *   setOpen(item.id)     setOpen(item.id)     setOpen(null)
 *
 *    ┌─────────────────────────────────────────────────────┐
 *    │ If hasChildren && open → Render ChildItem list       │
 *    │ ChildItem supports keyboard + click navigation       │
 *    └─────────────────────────────────────────────────────┘
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { ExpandLess, ExpandMore } from "components/icon/mui_icon";
import { SelectIcon } from "components/icon";
import StyledListItemButton from "./StyledListItemButton.style";

// ChildItem represents a submenu entry
const ChildItem = React.memo(({ item, parentItem, buttonClick, selected }) => {
  const path = useMemo(
    () => `/${parentItem.to}${!!item.to ? `/${item.to}` : ""}`,
    [parentItem, item],
  );

  const onKeyDown = (e) => {
    // Accessibility: Enter or Space triggers navigation
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      buttonClick(path);
    }
  };

  return (
    <ListItem
      disablePadding
      sx={{ display: "block", marginY: 0.25 }}
      role="none"
    >
      <StyledListItemButton
        sx={{ pl: 6.5, gap: "12px" }}
        selected={selected}
        onClick={() => buttonClick(path)}
        onKeyDown={onKeyDown}
        role="menuitem"
        tabIndex={0}
      >
        {/* Show icon if available */}
        {item.icon && (
          <ListItemIcon>
            <SelectIcon selected={selected} value={item.icon} />
          </ListItemIcon>
        )}

        {/* Display text */}
        <ListItemText
          primary={item.text}
          slotProps={{
            // MUI 9: typography props go through sx
            primary: { sx: { fontSize: 14, fontWeight: 600 } },
          }}
        />
      </StyledListItemButton>
    </ListItem>
  );
});

// ListItemCustom represents the top-level navigation entry
const ListItemCustom = React.memo((props) => {
  const { t } = useTranslation("layout");
  const { item, setIndex, miniSB, filterBar, buttonClick } = props;
  const location = useLocation();
  const selected = location.pathname.includes(item.check); // highlight active route
  const [open, setOpen] = useState(null); // track expanded/collapsed state

  const hasChildren = item.child && item.child.length > 0;

  // Handles click/keyboard interaction
  const handleClick = useCallback(() => {
    if (miniSB) {
      // Sidebar is minimized → only update selected index
      setIndex(item.id);
    } else if (hasChildren) {
      // Expand/Collapse child list
      setOpen((prev) => (prev === item.id ? null : item.id));
    } else {
      // Direct navigation (no children)
      buttonClick(`${!!item.to ? "/" : ""}${item.to}`, item.id);
    }
  }, [item, miniSB, setIndex, buttonClick, hasChildren]);

  // Sync open state with external conditions
  useEffect(() => {
    if (filterBar || selected) {
      setOpen(item.id); // expand if filtering or already selected
    } else if (miniSB) {
      setOpen(null); // collapse in mini sidebar
    }
  }, [filterBar, miniSB, selected, item.id]);

  // Keyboard accessibility
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(null);
    }
  };

  return (
    <ListItem disablePadding sx={{ display: "block" }} role="none">
      <StyledListItemButton
        role="menuitem"
        // icon only when the sidebar is collapsed: keep the item's name
        aria-label={miniSB ? item.text : undefined}
        aria-haspopup={hasChildren ? "true" : undefined}
        aria-expanded={hasChildren ? open === item.id : undefined}
        selected={selected}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
        sx={{ gap: "12px", justifyContent: "center" }}
      >
        {/* Icon always visible */}
        <ListItemIcon>
          <SelectIcon selected={selected} value={item.icon} />
        </ListItemIcon>

        {/* Text visible only if sidebar not minimized */}
        {!miniSB && (
          <ListItemText
            primary={item.text}
            slotProps={{
              // MUI 9: typography props go through sx
            primary: { sx: { fontSize: 14, fontWeight: 600 } },
            }}
          />
        )}

        {/* Expand/Collapse indicator */}
        {!miniSB &&
          !filterBar &&
          hasChildren &&
          (open === item.id ? <ExpandLess /> : <ExpandMore />)}
      </StyledListItemButton>

      {/* Render children if expanded */}
      {hasChildren && (
        <Collapse in={open === item.id && !miniSB} timeout="auto" unmountOnExit>
          <List
            disablePadding
            dense
            role="menu"
            aria-label={t("submenuLabel", { text: item.text })}
          >
            {item.child.map((childItem, idx) => (
              <ChildItem
                key={idx}
                item={childItem}
                parentItem={item}
                buttonClick={buttonClick}
                selected={location.pathname.includes(
                  `${item.check}${childItem.to !== "" ? `/${childItem.check}` : ""}`,
                )}
              />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
});

export default ListItemCustom;
